import React, { Fragment, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";

import { FuelProductIcon, ProductCartRedIcon } from "@components/Icons";

import type {
  ICartView,
  PaymentMethod,
} from "../../../model/product.cart.model";

import {
  CatalogOption,
  parseDotQuantity,
  useProductsStep,
} from "../../hook/useProductsStep";

import { formatBRL } from "@utils/currency";
import { cardOutlinedSx } from "../purchase/styles";

export function ProductsStepDesktop() {
  const { state, action } = useProductsStep();

  const isLoading = state.pricingStatus === "loading";
  const disabled = isLoading || !state.canInteract;

  const hasItems = state.cartView.length > 0;

  const [selected, setSelected] = useState<CatalogOption | null>(null);

  async function onSelect(opt: CatalogOption | null) {
    setSelected(opt);
    if (!opt) return;

    setSelected(null);

    void action.addProduct({
      productId: opt.key,
      code: opt.code ?? opt.key,
      description: opt.label,
      quantity: 0,
    });
  }

  function renderLoadingHint() {
    if (!isLoading) return null;
    return (
      <Box sx={{ mt: 1 }}>
        <LinearProgress />
        <Typography variant="caption" sx={{ opacity: 0.75 }}>
          Calculando preço e atualizando carrinho...
        </Typography>
      </Box>
    );
  }

  function renderEmptyState() {
    return (
      <TableRow>
        <TableCell
          data-col="estado-vazio"
          colSpan={9}
          sx={{ textAlign: "center", borderBottom: "none" }}
        >
          <ProductCartRedIcon style={{ fontSize: 100, margin: 30 }} />
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Nenhum produto adicionado.
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  function renderHeaderRow() {
    if (!hasItems) return null;

    return (
      <Fragment>
        <TableCell data-col="icone-produto" width={56} />
        <TableCell data-col="codigo-produto">Código</TableCell>
        <TableCell data-col="descricao-produto">Produto</TableCell>
        <TableCell data-col="valor-litro">Valor do Litro</TableCell>
        <TableCell data-col="subtotal">Subtotal</TableCell>
        <TableCell data-col="forma-pagamento">Forma de pagamento</TableCell>
        <TableCell data-col="quantidade">Quantidade</TableCell>
        <TableCell data-col="prazo-pagamento">Prazo</TableCell>
        <TableCell data-col="acoes" align="right" />
      </Fragment>
    );
  }

  function renderBodyRow(it: ICartView) {
    const qtyInvalid = !Number.isFinite(it.quantity) || it.quantity === 0;

    return (
      <TableRow key={it.id} data-row="item-carrinho">
        <TableCell data-col="icone-produto">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(255, 0, 0, 0.06)",
            }}
          >
            <FuelProductIcon fontSize="small" />
          </Box>
        </TableCell>

        <TableCell data-col="codigo-produto" sx={{ opacity: 0.9 }}>
          {it.code}
        </TableCell>

        <TableCell data-col="descricao-produto" sx={{ minWidth: 320 }}>
          <Typography fontWeight={500} sx={{ opacity: 0.9 }}>
            {it.description}
          </Typography>
        </TableCell>

        <TableCell data-col="valor-litro" sx={{ opacity: 0.9 }}>
          {formatBRL(it.displayUnitPrice, { fractionDigits: 4 })}
        </TableCell>

        <TableCell
          data-col="subtotal"
          sx={{ fontWeight: 700, color: "success.main" }}
        >
          {formatBRL(it.subtotal, { fractionDigits: 2 })}
        </TableCell>

        <TableCell data-col="forma-pagamento">
          <RadioGroup
            row
            value={it.payment}
            onChange={(e) =>
              void action.changePayment(it.id, e.target.value as PaymentMethod)
            }
          >
            <FormControlLabel
              value="T"
              control={
                <Radio
                  size="small"
                  sx={{
                    "&.Mui-checked": {
                      color: "error.main",
                    },
                    "&.Mui-disabled.Mui-checked": {
                      color: "error.main",
                    },
                  }}
                />
              }
              label="A vista"
              sx={{ mr: 2 }}
              disabled={true}
            />
            <FormControlLabel
              value="B"
              control={
                <Radio
                  size="small"
                  sx={{
                    "&.Mui-checked": {
                      color: "error.main",
                    },
                    "&.Mui-disabled.Mui-checked": {
                      color: "error.main",
                    },
                  }}
                />
              }
              label="Boleto"
              disabled={true}
            />
          </RadioGroup>
        </TableCell>

        <TableCell data-col="quantidade">
          <TextField
            select
            value={String(it.quantity)}
            onChange={(e) => void action.changeQty(it.id, e.target.value)}
            disabled={state.pricingStatus === "loading"}
            error={qtyInvalid}
            helperText={qtyInvalid ? "Selecione uma quantidade" : ""}
            sx={{
              minWidth: 120,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
          >
            {state.quantityOptions.map((opt) => (
              <MenuItem key={opt.value} value={parseDotQuantity(opt.label)}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </TableCell>

        <TableCell data-col="prazo-pagamento">
          <TextField
            select
            value={String(it.deadlinePayment)}
            onChange={(e) => void action.changeDeadline(it.id, e.target.value)}
            disabled={state.pricingStatus === "loading"}
            sx={{
              minWidth: 160,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
          >
            {it.paymentDayOptions.map((o) => {
              const days = Number(o.key);
              return (
                <MenuItem key={o.key} value={String(days)}>
                  {days === 0
                    ? "A vista"
                    : `${days} ${days === 1 ? "Dia" : "Dias"}`}
                </MenuItem>
              );
            })}
          </TextField>
        </TableCell>

        <TableCell data-col="acoes" align="right">
          <Typography
            component="button"
            onClick={() => action.remove(it.id)}
            sx={{
              border: 0,
              background: "transparent",
              cursor: "pointer",
              fontWeight: 700,
              color: "primary.main",
              opacity: 0.7,
            }}
          >
            Remover
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <Card variant="outlined" sx={cardOutlinedSx}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={2.25}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h5" fontWeight={700}>
              Produtos
            </Typography>

            {renderLoadingHint()}

            <IconButton aria-label="carrinho" disabled>
              <ShoppingCartOutlinedIcon />
            </IconButton>
          </Box>

          <TextField
            select
            label="Descrição do produto"
            value={selected?.key ?? ""}
            onChange={(e) => {
              const next =
                state.catalogOptions.find((o) => o.key === e.target.value) ??
                null;
              void onSelect(next);
            }}
            disabled={disabled}
            fullWidth
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
          >
            {state.catalogOptions.length === 0 ? (
              <MenuItem value="" disabled>
                Nenhum produto disponível
              </MenuItem>
            ) : (
              state.catalogOptions.map((opt) => (
                <MenuItem key={opt.key} value={opt.key}>
                  {opt.label}
                </MenuItem>
              ))
            )}
          </TextField>

          {!state.canInteract && (
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Selecione cliente, endereço e base antes de adicionar produtos.
            </Typography>
          )}

          {state.error && (
            <Typography variant="body2" color="error">
              {state.error}
            </Typography>
          )}

          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                minWidth: 980,
                "& th": { fontWeight: 700, whiteSpace: "nowrap" },
                "& td": { whiteSpace: "nowrap" },
              }}
            >
              <TableHead>
                <TableRow>{renderHeaderRow()}</TableRow>
              </TableHead>

              <TableBody>
                {!hasItems
                  ? renderEmptyState()
                  : state.cartView.map((item) => renderBodyRow(item))}
              </TableBody>
            </Table>
          </Box>

          <Divider />

          <Box sx={{ px: { lg: 15 } }}>
            <Typography
              variant="body1"
              textAlign="center"
              sx={{ opacity: 0.85 }}
            >
              <strong>Atenção:</strong> Para garantir melhor atendimento,
              recomendamos que os pedidos sejam programados com pelo menos 24h
              de antecedência. Os valores informados refletem os preços do dia e
              estão sujeitos a alterações até a data do faturamento.
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
